import { z } from "zod";

// ── FAQ (#735): categories and questions managed by admins, read by every user ──

export const FAQ_CATEGORY_NAME_MAX_LENGTH = 80;
export const FAQ_QUESTION_MAX_LENGTH = 250;
export const FAQ_ANSWER_MAX_LENGTH = 20000;

// A client's search that found nothing is recorded for the admin (#735) once it is
// long enough to mean something: the MIN applies to the normalised query (accents
// stripped, whitespace collapsed), the MAX to what was typed, trimmed.
export const FAQ_SEARCH_MISS_MIN_LENGTH = 3;
export const FAQ_SEARCH_MISS_MAX_LENGTH = 120;

// Rich-text allowlist for an answer. The backend sanitises every write against it
// and the frontend re-sanitises before rendering: both sides read these constants
// so they cannot drift. Links only keep `href` (http, https, mailto); `target` and
// `rel` are forced by the sanitiser.
export const FAQ_ANSWER_ALLOWED_TAGS = [
    "h2", "h3", "p", "br", "strong", "em", "u", "s", "ul", "ol", "li", "blockquote", "a",
] as const;
export const FAQ_ANSWER_ALLOWED_LINK_SCHEMES = ["http", "https", "mailto"] as const;

// ── Entities (admin view) ──

export const FaqQuestionSchema = z.object({
    id: z.string().uuid(),
    category_id: z.string().uuid(),
    question: z.string(),
    answer_html: z.string(),
    position: z.number().int(),
    is_published: z.boolean(),
    updated_at: z.string(),
    updated_by: z.string().nullable(),
});
export type FaqQuestion = z.infer<typeof FaqQuestionSchema>;

export const FaqCategorySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    position: z.number().int(),
    is_visible: z.boolean(),
    updated_at: z.string(),
    updated_by: z.string().nullable(),
});
export type FaqCategory = z.infer<typeof FaqCategorySchema>;

// Whole FAQ for the admin page, in one call: every non-deleted category (hidden
// ones included) with every non-deleted question (drafts included), both ordered
// by position.
export const FaqAdminCategorySchema = FaqCategorySchema.extend({
    questions: z.array(FaqQuestionSchema),
});
export type FaqAdminCategory = z.infer<typeof FaqAdminCategorySchema>;

export const FaqAdminTreeSchema = z.object({
    categories: z.array(FaqAdminCategorySchema),
});
export type FaqAdminTree = z.infer<typeof FaqAdminTreeSchema>;

// ── Public view (any signed-in user) ──

// Only visible categories holding at least one published question, and only
// published questions; already ordered, so no position or audit field.
export const FaqPublicQuestionSchema = z.object({
    id: z.string().uuid(),
    question: z.string(),
    answer_html: z.string(),
});
export type FaqPublicQuestion = z.infer<typeof FaqPublicQuestionSchema>;

export const FaqPublicCategorySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    questions: z.array(FaqPublicQuestionSchema),
});
export type FaqPublicCategory = z.infer<typeof FaqPublicCategorySchema>;

export const FaqPublicSchema = z.object({
    categories: z.array(FaqPublicCategorySchema),
});
export type FaqPublic = z.infer<typeof FaqPublicSchema>;

// ── Requests ──

export const CreateFaqCategoryRequestSchema = z.object({
    name: z.string().trim().min(1).max(FAQ_CATEGORY_NAME_MAX_LENGTH),
    is_visible: z.boolean().optional(),
});
export type CreateFaqCategoryRequest = z.infer<typeof CreateFaqCategoryRequestSchema>;

export const UpdateFaqCategoryRequestSchema = z.object({
    name: z.string().trim().min(1).max(FAQ_CATEGORY_NAME_MAX_LENGTH).optional(),
    is_visible: z.boolean().optional(),
});
export type UpdateFaqCategoryRequest = z.infer<typeof UpdateFaqCategoryRequestSchema>;

export const CreateFaqQuestionRequestSchema = z.object({
    category_id: z.string().uuid(),
    question: z.string().trim().min(1).max(FAQ_QUESTION_MAX_LENGTH),
    answer_html: z.string().max(FAQ_ANSWER_MAX_LENGTH),
    is_published: z.boolean().optional(),
});
export type CreateFaqQuestionRequest = z.infer<typeof CreateFaqQuestionRequestSchema>;

// Changing the category goes through the move endpoint, not this one.
export const UpdateFaqQuestionRequestSchema = z.object({
    question: z.string().trim().min(1).max(FAQ_QUESTION_MAX_LENGTH).optional(),
    answer_html: z.string().max(FAQ_ANSWER_MAX_LENGTH).optional(),
    is_published: z.boolean().optional(),
});
export type UpdateFaqQuestionRequest = z.infer<typeof UpdateFaqQuestionRequestSchema>;

// Move a question into a category (the same one or another) at `index` among that
// category's questions; an index past the end appends. Serves both "Move to…"
// (index = end) and a drag across categories.
export const MoveFaqQuestionRequestSchema = z.object({
    category_id: z.string().uuid(),
    index: z.number().int().min(0),
});
export type MoveFaqQuestionRequest = z.infer<typeof MoveFaqQuestionRequestSchema>;

// Full ordered id list of the scope (all categories, or one category's questions).
// It must be exactly the current set, otherwise the list is stale → 400.
export const ReorderFaqRequestSchema = z.object({
    ordered_ids: z.array(z.string().uuid()).max(500),
});
export type ReorderFaqRequest = z.infer<typeof ReorderFaqRequestSchema>;

// ── Search misses (#735): searches of the client FAQ that found nothing ──

// The client sends what it typed; the backend trims, normalises and checks the
// length rules (the MIN needs normalising, so it is not repeated here).
export const RecordFaqSearchMissRequestSchema = z.object({
    query: z.string().max(FAQ_SEARCH_MISS_MAX_LENGTH),
});
export type RecordFaqSearchMissRequest = z.infer<typeof RecordFaqSearchMissRequestSchema>;

// One open search, grouped by its normalised form over the recent window:
// `query` is the latest wording typed, `licence_count` the distinct licences.
export const FaqSearchMissSchema = z.object({
    normalized_query: z.string(),
    query: z.string(),
    search_count: z.number().int(),
    licence_count: z.number().int(),
    last_searched_at: z.string(),
});
export type FaqSearchMiss = z.infer<typeof FaqSearchMissSchema>;

export const FaqSearchMissListSchema = z.object({
    items: z.array(FaqSearchMissSchema),
});
export type FaqSearchMissList = z.infer<typeof FaqSearchMissListSchema>;

// Hide every open row of one normalised query from the admin list.
export const DismissFaqSearchMissRequestSchema = z.object({
    normalized_query: z.string().min(1).max(FAQ_SEARCH_MISS_MAX_LENGTH),
});
export type DismissFaqSearchMissRequest = z.infer<typeof DismissFaqSearchMissRequestSchema>;

// The stamp the dismiss put on its rows: the Undo hands it back to restore them
// (and only them: a group dismissed twice keeps its earlier dismissal).
export const DismissFaqSearchMissResultSchema = z.object({
    dismissed_at: z.string(),
});
export type DismissFaqSearchMissResult = z.infer<typeof DismissFaqSearchMissResultSchema>;

// The Undo of a dismiss: puts back the rows of `normalized_query` stamped at `dismissed_at`.
export const RestoreFaqSearchMissRequestSchema = z.object({
    normalized_query: z.string().min(1).max(FAQ_SEARCH_MISS_MAX_LENGTH),
    dismissed_at: z.string().datetime(),
});
export type RestoreFaqSearchMissRequest = z.infer<typeof RestoreFaqSearchMissRequestSchema>;

// ── HTTP envelopes ({ success, data }) ──

export const FaqPublicDetailResponseSchema = z.object({
    success: z.boolean(),
    data: FaqPublicSchema,
});
export type FaqPublicDetailResponse = z.infer<typeof FaqPublicDetailResponseSchema>;

export const FaqAdminTreeDetailResponseSchema = z.object({
    success: z.boolean(),
    data: FaqAdminTreeSchema,
});
export type FaqAdminTreeDetailResponse = z.infer<typeof FaqAdminTreeDetailResponseSchema>;

export const FaqCategoryDetailResponseSchema = z.object({
    success: z.boolean(),
    data: FaqCategorySchema,
});
export type FaqCategoryDetailResponse = z.infer<typeof FaqCategoryDetailResponseSchema>;

export const FaqQuestionDetailResponseSchema = z.object({
    success: z.boolean(),
    data: FaqQuestionSchema,
});
export type FaqQuestionDetailResponse = z.infer<typeof FaqQuestionDetailResponseSchema>;

export const FaqSearchMissListDetailResponseSchema = z.object({
    success: z.boolean(),
    data: FaqSearchMissListSchema,
});
export type FaqSearchMissListDetailResponse = z.infer<typeof FaqSearchMissListDetailResponseSchema>;

export const DismissFaqSearchMissDetailResponseSchema = z.object({
    success: z.boolean(),
    data: DismissFaqSearchMissResultSchema,
});
export type DismissFaqSearchMissDetailResponse = z.infer<typeof DismissFaqSearchMissDetailResponseSchema>;
